import type { Character } from '../domain/character.ts';
import type { PortableCharacterFile } from './format.ts';
import { PORTABLE_FORMAT, PORTABLE_SCHEMA_VERSION, PORTABLE_FILE_EXTENSION } from './format.ts';
import type { CustomContentSource, PortableCustomContent } from './customDependencies.ts';
import { collectCustomDependencies, emptyCustomContent } from './customDependencies.ts';
import { createCustomSkillLibrary, validateCustomSkillLibrary } from '../domain/customSkill.ts';

export interface ExportOptions {
  rulesetVersion: string;
  appVersion: string;
  exportedAt: string;
}

function envelope(
  character: Character,
  options: ExportOptions,
  customContent: PortableCustomContent,
): PortableCharacterFile {
  return {
    format: PORTABLE_FORMAT,
    schemaVersion: PORTABLE_SCHEMA_VERSION,
    rulesetVersion: options.rulesetVersion,
    appVersion: options.appVersion,
    exportedAt: options.exportedAt,
    character,
    customContent,
  };
}

/**
 * Builds a file for a crawler that needs no custom content.
 *
 * Kept for callers that have no catalogue to hand. Anything that might
 * reference custom content must use `buildPortableExport`, which refuses rather
 * than shipping a package missing one of its dependencies.
 */
export function buildPortableFile(
  character: Character,
  options: ExportOptions,
): PortableCharacterFile {
  return envelope(character, options, emptyCustomContent());
}

export type PortableExportOutcome =
  | { ok: true; file: PortableCharacterFile }
  | { ok: false; missing: string[]; message: string };

/**
 * Builds a SELF-CONTAINED file, or refuses.
 *
 * RULE DCC-PORT-001 - an export either carries every custom definition the
 * crawler needs, or it is not produced at all.
 *
 * The refusal matters more than it looks. A package that quietly omits a
 * definition still opens on the receiving machine and still produces a crawler,
 * just one missing a Skill or unable to reverse part of its own history. That
 * failure is silent and arrives later, on someone else's computer. Refusing
 * happens here, in front of the person who can fix it.
 */
export function buildPortableExport(
  character: Character,
  options: ExportOptions,
  source: CustomContentSource,
): PortableExportOutcome {
  const { content, missing } = collectCustomDependencies(character, source);

  if (missing.length > 0) {
    return {
      ok: false,
      missing,
      message:
        `${character.name} refers to custom content this installation does not have, so no ` +
        'file was written. Exporting anyway would produce a crawler that arrives broken. ' +
        `Missing: ${missing.join(', ')}.`,
    };
  }

  // Skill + optional AttackProfile is one logical definition. Re-wrap the
  // carried pair in the SAME schema-3 validator used on disk; an inconsistent
  // live source must not be silently downgraded to a manual Skill in transit.
  const validation = validateCustomSkillLibrary({
    ...createCustomSkillLibrary('1970-01-01T00:00:00.000Z'),
    skills: content.skills,
    attacks: content.attacks,
  });
  if (!validation.valid) {
    return {
      ok: false,
      missing: [],
      message:
        `${character.name} depends on custom Skill automation that cannot be exported ` +
        `consistently: ${validation.problems.join(' ')}`,
    };
  }

  return { ok: true, file: envelope(character, options, content) };
}

export function serializePortableFile(file: PortableCharacterFile): string {
  return JSON.stringify(file, null, 2);
}

export function suggestedFileName(character: Character): string {
  const safe =
    character.name
      .replace(/[^A-Za-z0-9 _-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'crawler';
  return `${safe}${PORTABLE_FILE_EXTENSION}`;
}
